import classNames from 'classnames';
import { MouseEvent, useCallback, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Card } from '~/components/ui/Card';
import { Checkbox, CheckboxState } from '~/components/ui/nucleus/Checkbox';
import { ListAssetsQuery } from '~/generated/graphql';
import { Status } from '~/gql/graphql';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { Avatar } from '../ui/Avatar';
import { Image } from '../ui/Image';
import { StatusBadge } from '../ui/StatusBadge';

type Asset = ListAssetsQuery['assets'][number];

// Long press configuration
const LONG_PRESS_DURATION_MS = 400;
const MOVE_TOLERANCE_PX = 10;

type AssetListItemProps = {
  asset: Asset;
  selected?: boolean;
  isSelectionMode?: boolean;
  onSelect?: (id: string, shift: boolean, single?: boolean) => void;
};

const AssetListItem = ({
  asset,
  selected,
  isSelectionMode,
  onSelect,
}: AssetListItemProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  // Check if the current path matches /assets/:id exactly or has a trailing slash/path segment
  // Using regex to ensure we match the exact ID and not partial matches (e.g., /assets/1 shouldn't match /assets/17)
  // The pattern also handles nested routes like /assets/:id/edit, /assets/:id/deploy, etc.
  const assetPathPattern = new RegExp(`^/assets/${asset.id}(?:/|$)`);
  const active = assetPathPattern.test(location.pathname);
  const deployment = asset.deployment;

  // Long press refs for mobile
  const longPressTimeoutRef = useRef<number | null>(null);
  const longPressActivatedRef = useRef(false);
  const hasTouchMovedRef = useRef(false);
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);
  const touchStartTimeRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);

  // Handle long press start (mobile)
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!isMobile || !onSelect) return;

      const touch = e.touches[0];
      if (!touch) return;

      // Prevent default immediately to stop browser preview/context menu
      e.preventDefault();

      // Clear any existing timeout
      if (longPressTimeoutRef.current) {
        window.clearTimeout(longPressTimeoutRef.current);
        longPressTimeoutRef.current = null;
      }

      longPressActivatedRef.current = false;
      hasTouchMovedRef.current = false;
      touchStartTimeRef.current = Date.now();
      touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };

      // Start long press timer
      longPressTimeoutRef.current = window.setTimeout(() => {
        // Only proceed if component is still mounted
        if (!isMountedRef.current) return;

        longPressActivatedRef.current = true;
        // Trigger haptic feedback if available (only while mounted)
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }
        // Select this asset (single selection to start selection mode)
        onSelect(asset.id, false, true);
      }, LONG_PRESS_DURATION_MS);
    },
    [isMobile, onSelect, asset.id]
  );

  // Handle touch move (cancel long press if user scrolls)
  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isMobile) return;

      const start = touchStartPosRef.current;
      const touch = e.touches[0];

      if (!start || !touch) return;

      const dx = Math.abs(touch.clientX - start.x);
      const dy = Math.abs(touch.clientY - start.y);

      // Cancel long press if user moves beyond tolerance (scrolling)
      if (dx > MOVE_TOLERANCE_PX || dy > MOVE_TOLERANCE_PX) {
        if (longPressTimeoutRef.current) {
          window.clearTimeout(longPressTimeoutRef.current);
          longPressTimeoutRef.current = null;
        }
        hasTouchMovedRef.current = true;
      }
    },
    [isMobile]
  );

  // Handle touch end
  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!isMobile) return;

      // Clear timeout and reset refs
      if (longPressTimeoutRef.current) {
        window.clearTimeout(longPressTimeoutRef.current);
        longPressTimeoutRef.current = null;
      }

      // If user scrolled, reset refs and return
      if (hasTouchMovedRef.current) {
        hasTouchMovedRef.current = false;
        touchStartTimeRef.current = null;
        touchStartPosRef.current = null;
        return;
      }

      // If long press already activated, prevent navigation and reset
      if (longPressActivatedRef.current) {
        e.preventDefault();
        e.stopPropagation();
        setTimeout(() => {
          longPressActivatedRef.current = false;
        }, 0);
        touchStartTimeRef.current = null;
        touchStartPosRef.current = null;
        return;
      }

      // If we're in selection mode, toggle selection instead of navigating
      if (isSelectionMode && onSelect) {
        e.preventDefault();
        e.stopPropagation();
        onSelect(asset.id, false);
        touchStartTimeRef.current = null;
        touchStartPosRef.current = null;
        return;
      }

      // Quick tap - manually navigate since we prevented default
      // Only navigate if touchStartTimeRef was set (valid touch sequence)
      if (touchStartTimeRef.current) {
        const touchDuration = Date.now() - touchStartTimeRef.current;
        // Only navigate if it was a quick tap (less than long press duration)
        if (touchDuration < LONG_PRESS_DURATION_MS) {
          e.preventDefault();
          e.stopPropagation();
          navigate('/assets/' + asset.id);
        }
      }
      touchStartTimeRef.current = null;
      touchStartPosRef.current = null;
    },
    [isMobile, isSelectionMode, onSelect, asset.id, navigate]
  );

  // Handle touch cancel (e.g., system gesture interrupts)
  const handleTouchCancel = useCallback(() => {
    if (!isMobile) return;

    // Clean up all refs and timeouts
    if (longPressTimeoutRef.current) {
      window.clearTimeout(longPressTimeoutRef.current);
      longPressTimeoutRef.current = null;
    }
    longPressActivatedRef.current = false;
    hasTouchMovedRef.current = false;
    touchStartTimeRef.current = null;
    touchStartPosRef.current = null;
  }, [isMobile]);

  // Cleanup timeout and reset refs on unmount to prevent memory leaks
  // and avoid calling onSelect on an unmounted component
  useEffect(() => {
    return () => {
      // Mark component as unmounted to prevent timeout callback from executing
      isMountedRef.current = false;

      // Clear timeout if it exists
      if (longPressTimeoutRef.current !== null) {
        window.clearTimeout(longPressTimeoutRef.current);
        longPressTimeoutRef.current = null;
      }

      // Reset all refs to prevent stale state
      longPressActivatedRef.current = false;
      hasTouchMovedRef.current = false;
      touchStartTimeRef.current = null;
      touchStartPosRef.current = null;
    };
  }, []);

  // Handle click for desktop (and synthetic clicks on mobile)
  // React Router's Link onClick receives React.MouseEvent for both mouse and keyboard events
  // Card click behavior: select ONLY this asset and navigate to it
  // Checkbox click behavior: toggle selection (handled separately in handleCheckboxClick)
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // Prevent navigation if long-press was just activated (mobile synthetic click)
      // This guards against synthetic click events fired after touch events
      if (longPressActivatedRef.current) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Don't handle clicks if clicking on checkbox area - let checkbox handler deal with it
      const target = e.target as HTMLElement;
      if (
        target.closest('.form-checkbox') ||
        target.closest('input[type="checkbox"]')
      ) {
        return;
      }

      if (!onSelect) return;

      // Shift-click for range selection (prevent navigation)
      if (e.shiftKey) {
        e.preventDefault();
        onSelect(asset.id, true);
        return;
      }

      // Normal card click on desktop: select ONLY this asset, then navigate
      // This replaces any existing selection with just this asset
      // Navigation proceeds (no preventDefault)
      if (!isMobile) {
        onSelect(asset.id, false, true); // single=true replaces selection
      }
    },
    [onSelect, asset.id, isMobile]
  );

  // Handle keyboard events (Enter/Space) to prevent navigation after long-press
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Prevent navigation if long-press was just activated
    if (longPressActivatedRef.current) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
      }
      return;
    }
  }, []);

  // Handle checkbox click (desktop only)
  const handleCheckboxClick = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (e: MouseEvent, _newValue: CheckboxState) => {
      e.stopPropagation();
      onSelect?.(asset.id, e.shiftKey);
    },
    [onSelect, asset.id]
  );

  // Prevent context menu on mobile to avoid browser preview
  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (isMobile) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [isMobile]
  );

  return (
    <div className='flex flex-1 items-stretch gap-2 overflow-x-hidden'>
      {/* Checkbox for desktop - only show when selection is enabled */}
      {onSelect && (
        <div className='mb-3 hidden w-6 shrink-0 items-center justify-start overflow-hidden p-1 lg:flex'>
          <Checkbox
            value={selected ? CheckboxState.CHECKED : CheckboxState.UNCHECKED}
            className='disabled:border-grey-20 disabled:bg-grey-10'
            onClick={handleCheckboxClick}
          />
        </div>
      )}

      <Card className='w-full lg:max-w-[calc(100%-32px)]'>
        <Link
          to={'/assets/' + asset.id}
          className={classNames(
            'mb-3 flex flex-1 items-center gap-2 rounded border px-2 py-2 shadow-sm transition-all duration-200 lg:px-4 lg:py-3',
            {
              'bg-brand/5 border-brand bg-brand-ghost shadow-md':
                active || selected,
              'border-grey-20 bg-white hover:shadow-md': !active && !selected,
            },
            // Prevent text selection and browser preview on mobile
            isMobile && 'select-none [-webkit-touch-callout:none]'
          )}
          style={
            isMobile
              ? {
                  touchAction: 'manipulation',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  WebkitTouchCallout: 'none',
                }
              : undefined
          }
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchCancel}
          onContextMenu={handleContextMenu}
        >
          {/* Image */}
          <div className='hidden h-9 w-9 xs:block lg:h-14 lg:w-14'>
            {asset.image || deployment?.image ? (
              <Image url={asset.image ?? deployment?.image} size='fit' />
            ) : (
              <Avatar
                image={asset.image}
                name={asset.name}
                size='full'
                square
                className='rounded-md'
              />
            )}
          </div>

          {/* Content */}
          <div className='ml-2 mr-3 flex-1 overflow-hidden'>
            <div className='items-center gap-2 md:flex'>
              <div className='mb-0 flex-shrink flex-grow-0 text-sm font-semibold leading-4 text-grey-70 md:text-base lg:max-w-[70%] xl:mb-0'>
                <div className='truncate'>{asset.name}</div>
              </div>
              {deployment?.displayLocation && (
                <>
                  <div className='mt-1 hidden h-1 w-1 rounded-full bg-grey-90 md:block' />
                  <div className='secondary-text mb-0 flex-1 flex-grow truncate text-sm text-grey-40 md:mt-0 md:text-base'>
                    {deployment.displayLocation}
                  </div>
                </>
              )}
            </div>
            <div className='tertiary-text hidden truncate text-xs text-grey-40 md:block'>
              {asset.type ?? ''}
            </div>
          </div>

          {/* Status badge for inactive assets */}
          <div className='flex h-full items-center gap-3'>
            {asset.status === Status.Inactive && (
              <StatusBadge value={asset.status} />
            )}
          </div>
        </Link>
      </Card>
    </div>
  );
};

type AssetListProps = {
  data: Asset[];
  selected?: string[];
  toggleSelected?: (id: string, shift: boolean, single?: boolean) => void;
};

export const AssetList = ({
  data,
  selected,
  toggleSelected,
}: AssetListProps) => {
  // Determine if we're in selection mode (any asset selected)
  const isSelectionMode = selected && selected.length > 0;

  return (
    <>
      {data.map((asset) => (
        <AssetListItem
          key={asset.id}
          asset={asset}
          selected={selected?.includes(asset.id)}
          isSelectionMode={isSelectionMode}
          onSelect={toggleSelected}
        />
      ))}
    </>
  );
};
